// Build a set of "Learn more" reference links for a given plant + user state.
// All URLs are search-based so we don't need per-species IDs — the user gets to
// the site's search page pre-filled with the plant's name.
//
// Uses scientific_name when available (more precise), falls back to common_name.

import { stateCode } from "./usStates";

const encode = (s) => encodeURIComponent((s || "").trim());

// State → known native-plant / wildflower reference site. Only listed when
// the site's search is usable via `site:` restricted Google search.
// For anything not listed, we simply omit the state-specific link.
const STATE_REFERENCE_SITES = {
  IL: { host: "illinoiswildflowers.info", label: "Illinois Wildflowers" },
  MN: { host: "minnesotawildflowers.info", label: "Minnesota Wildflowers" },
  MI: { host: "michiganflora.net", label: "Michigan Flora" },
  FL: { host: "floridawildflowers.com", label: "Florida Wildflowers" },
  CA: { host: "calflora.org", label: "Calflora" },
  TX: { host: "wildflower.org", label: "Lady Bird Johnson Wildflower Center" },
  // Native Plant Trust's Go Botany covers all of New England + NY.
  MA: { host: "gobotany.nativeplanttrust.org", label: "Go Botany (New England)" },
  CT: { host: "gobotany.nativeplanttrust.org", label: "Go Botany (New England)" },
  ME: { host: "gobotany.nativeplanttrust.org", label: "Go Botany (New England)" },
  NH: { host: "gobotany.nativeplanttrust.org", label: "Go Botany (New England)" },
  RI: { host: "gobotany.nativeplanttrust.org", label: "Go Botany (New England)" },
  VT: { host: "gobotany.nativeplanttrust.org", label: "Go Botany (New England)" },
  NY: { host: "gobotany.nativeplanttrust.org", label: "Go Botany (New England)" },
};

/**
 * @param {object} args
 * @param {{common_name?: string, scientific_name?: string, is_keystone?: boolean}} args.identification
 * @param {string} [args.state] — user's state, e.g. "Illinois"
 * @returns {Array<{ key: string, label: string, url: string }>}
 */
export const buildPlantLinks = ({ identification = {}, state } = {}) => {
  const common = (identification.common_name || "").trim();
  const scientific = (identification.scientific_name || "").trim();
  const nameForSearch = scientific || common;
  if (!nameForSearch) return [];

  // Ignore the placeholder "Unknown" / "Desconocido" used for drafts / low-confidence.
  const lower = (common || "").toLowerCase();
  if (!scientific && (lower === "unknown" || lower === "desconocido" || !common)) {
    return [];
  }

  const q = encode(nameForSearch);
  const qBroad = encode(scientific ? `${scientific} ${common}` : common);

  const links = [
    {
      key: "wikipedia",
      label: "Wikipedia",
      url: `https://en.wikipedia.org/wiki/Special:Search?search=${q}`,
    },
    {
      key: "usda",
      label: "USDA PLANTS",
      url: `https://plants.usda.gov/home/plantProfile?symbol=&search=${q}`,
    },
    {
      key: "inaturalist",
      label: "iNaturalist",
      url: `https://www.inaturalist.org/taxa/search?q=${q}`,
    },
  ];

  // State-specific reference (via a Google site: search so we don't need
  // per-site URL formats).
  const code = stateCode(state);
  const site = STATE_REFERENCE_SITES[code];
  if (site) {
    links.push({
      key: "state",
      label: site.label,
      url: `https://www.google.com/search?q=${encode(`site:${site.host} ${nameForSearch}`)}`,
    });
  }

  // Xerces — only surface for keystone / pollinator plants.
  if (identification.is_keystone) {
    links.push({
      key: "xerces",
      label: "Xerces Society",
      url: `https://xerces.org/search?keys=${q}`,
    });
  }

  // Generic search + image search — always last, for visual confirmation.
  links.push({
    key: "google",
    label: "Google",
    url: `https://www.google.com/search?q=${qBroad}`,
  });
  links.push({
    key: "google_images",
    label: "Google Images",
    url: `https://www.google.com/search?tbm=isch&q=${qBroad}`,
  });

  return links;
};
